import { EventType, PublicClientApplication, type AccountInfo, type AuthenticationResult, type Configuration } from "@azure/msal-browser";

// msalInstance and loginRequest are initialised by initMsal() in main.tsx before
// React renders. All other modules import these as live bindings — they will have
// their final values by the time any runtime code reads them.
export let msalInstance: PublicClientApplication;
export let loginRequest: { scopes: string[] };

/**
 * Creates the MSAL PublicClientApplication and login request for the given
 * client ID and authority. Called once at startup, before React renders.
 * Falls back to the platform's own env-var credentials when no white-label
 * Entra app is registered for the current domain.
 */
export function initMsal(clientId: string, authority: string): PublicClientApplication {
  const config: Configuration = {
    auth: {
      clientId,
      authority,
      // Use the current origin so the auth code returns to whichever domain
      // the user started on. All valid origins must be registered as SPA
      // redirect URIs in the Entra app registration.
      redirectUri: window.location.origin,
    },
    cache: {
      // sessionStorage: MSAL's cache holds Entra access and refresh tokens in plain text,
      // so it lives with the tab, never in localStorage where every tab, a reopened
      // browser and any injected script could read it for days. A new tab is signed in
      // again silently from the Entra session (restoreEntraSession below).
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
  };

  msalInstance = new PublicClientApplication(config);
  loginRequest = { scopes: [`api://${clientId}/access_as_user`] };
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.SSO_SILENT_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      const account = (event.payload as AuthenticationResult | null)?.account;
      if (account) rememberEntraHint(account);
    }
    if (event.eventType === EventType.LOGOUT_SUCCESS || event.eventType === EventType.LOGOUT_END) forgetEntraHint();
  });
  return msalInstance;
}

// ── New tabs: a silent sign-in instead of a shared token cache ───────────────
//
// With the token cache per tab, what a new tab needs is only a hint that this browser
// is signed in to Entra, and as whom. ENTRA_HINT holds the account's sign-in name and
// nothing else — it opens no session by itself. The tab asks Entra for tokens with it
// (ssoSilent, answered from Entra's own session cookie); if the browser blocks that, the
// person sees the sign-in page, where one click finishes the same thing.
const ENTRA_HINT = "abacas.entraHint";

function rememberEntraHint(account: AccountInfo) {
  msalInstance.setActiveAccount(account);
  try { localStorage.setItem(ENTRA_HINT, account.username); } catch { /* storage unavailable */ }
}

/** Called on every sign-out path; the other tabs see the hint go and sign out too. */
export function forgetEntraHint() {
  try { localStorage.removeItem(ENTRA_HINT); } catch { /* ignore */ }
}

export const ENTRA_HINT_KEY = ENTRA_HINT;

/**
 * Before React renders: drops any token cache an earlier version left in localStorage,
 * then, in a tab with no Entra account of its own but a browser that is signed in, asks
 * Entra for the session silently. Never throws; a failure just leaves the tab signed out.
 */
export async function restoreEntraSession(instance: PublicClientApplication, clientId: string): Promise<void> {
  let hint: string | null = null;
  try {
    hint = localStorage.getItem(ENTRA_HINT);
    // The earlier localStorage cache: carry its account name across as the hint, then remove every entry.
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith("msal.") || key.includes(clientId) || key.includes("login.microsoftonline.com") || key.includes("login.windows.net")) stale.push(key);
    }
    for (const key of stale) {
      if (!hint) {
        try {
          const value = JSON.parse(localStorage.getItem(key) ?? "null") as { username?: unknown; credentialType?: unknown } | null;
          if (value && typeof value.username === "string" && !value.credentialType) hint = value.username;
        } catch { /* not an account entry */ }
      }
      localStorage.removeItem(key);
    }
    if (hint) localStorage.setItem(ENTRA_HINT, hint);
  } catch { /* storage unavailable */ }

  // A redirect coming back from Entra is MsalProvider's to finish.
  if (window.location.hash.includes("code=") || window.location.hash.includes("error=")) return;
  if (!hint || instance.getAllAccounts().length > 0) return;
  try {
    const result = await instance.ssoSilent({ ...loginRequest, loginHint: hint });
    instance.setActiveAccount(result.account);
  } catch {
    // Third-party cookies blocked, the Entra session ended, or consent needed: the sign-in page takes over.
  }
}
